import { DateTime } from 'luxon'

/** Same zone as backend `kyivSubmissionBounds.js` (DST-aware). */
const KYIV = 'Europe/Kyiv'

/** Parse `created_at` from SQLite/API (UTC ISO or `YYYY-MM-DD HH:mm:ss`). */
export function parseStoredCreatedAt(dateStr: string): DateTime {
  if (!dateStr) return DateTime.invalid('empty')
  let dt = DateTime.fromISO(dateStr, { zone: 'utc' })
  if (dt.isValid) return dt
  dt = DateTime.fromSQL(dateStr, { zone: 'utc' })
  if (dt.isValid) return dt
  const js = new Date(dateStr)
  if (!Number.isNaN(js.getTime())) {
    return DateTime.fromJSDate(js, { zone: 'utc' })
  }
  return DateTime.invalid('unparsable')
}

/** Human-readable date+time in Kyiv (admin table & export). */
export function formatSubmissionDateTimeKyiv(dateStr: string): string {
  const dt = parseStoredCreatedAt(dateStr)
  if (!dt.isValid) return dateStr
  return dt.setZone(KYIV).toFormat('dd.MM.yyyy, HH:mm')
}

/** Two lines: `28.04.2026,` and `14:30` (Kyiv) for compact table cells. */
export function formatSubmissionDateTimeKyivLines(dateStr: string): { dateLine: string; timeLine: string } {
  const dt = parseStoredCreatedAt(dateStr)
  if (!dt.isValid) return { dateLine: dateStr, timeLine: '' }
  const z = dt.setZone(KYIV)
  return {
    dateLine: `${z.toFormat('dd.MM.yyyy')},`,
    timeLine: z.toFormat('HH:mm')
  }
}

/** Single string with newline between date and time (use with `whitespace-pre-line`). */
export function formatSubmissionDateTimeKyivMultiline(dateStr: string): string {
  const { dateLine, timeLine } = formatSubmissionDateTimeKyivLines(dateStr)
  if (timeLine) return `${dateLine}\n${timeLine}`
  return formatSubmissionDateTimeKyiv(dateStr)
}

export function formatFormCreatedDateKyiv(dateStr: string): string {
  const dt = parseStoredCreatedAt(dateStr)
  if (!dt.isValid) return dateStr
  return dt.setZone(KYIV).toFormat('dd.MM.yyyy')
}

/** Calendar date `YYYY-MM-DD` in Kyiv (for export filenames, etc.). */
export function kyivCalendarDateFromStored(dateStr: string): string {
  const dt = parseStoredCreatedAt(dateStr)
  if (!dt.isValid) return ''
  return dt.setZone(KYIV).toFormat('yyyy-MM-dd')
}

export function kyivTodayDateString(): string {
  return DateTime.now().setZone(KYIV).toFormat('yyyy-MM-dd')
}

/** Min/max calendar dates (Kyiv) from submission rows; `null` if no valid rows. */
export function kyivDateRangeFromSubmissions(subs: { created_at: string }[]): { from: string; to: string } | null {
  if (!subs.length) return null
  let minMs = Infinity
  let maxMs = -Infinity
  let minAt = ''
  let maxAt = ''
  for (const s of subs) {
    const dt = parseStoredCreatedAt(s.created_at)
    if (!dt.isValid) continue
    const ms = dt.toMillis()
    if (ms < minMs) {
      minMs = ms
      minAt = s.created_at
    }
    if (ms > maxMs) {
      maxMs = ms
      maxAt = s.created_at
    }
  }
  if (!minAt || !maxAt) return null
  return {
    from: kyivCalendarDateFromStored(minAt),
    to: kyivCalendarDateFromStored(maxAt)
  }
}

export function submissionCellValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return value ? 'Так' : 'Ні'
  if (Array.isArray(value)) return value.map(String).join(', ')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}
